/**
 * Lifetime launch-window countdown — pure policy (fully unit-tested).
 *
 * The count of sold lifetime passes lives in Firestore at `stats/lifetime`
 * ({ sold: number }), publicly readable, writable only server-side (console
 * now; RevenueCat webhook → Cloud Function at launch). The paywall:
 *
 * - Below the threshold: no countdown (scarcity isn't credible at 12/500,
 *   and fake-feeling urgency damages trust).
 * - From SHOW_THRESHOLD on (350 sold, 150 left — Chad, 17 Oct 2026; was 200):
 *   "Only N of 500 left" — true, live scarcity.
 * - At the cap: the lifetime tier disappears from the paywall entirely —
 *   the sunset enforces itself with no app update.
 */

export const LIFETIME_CAP = 500;
export const LIFETIME_SHOW_THRESHOLD = 350;

export interface LifetimeCountdown {
  /** Render the "Only N of 500 left" badge? */
  showCountdown: boolean;
  /** Passes still available (0 when sold out). */
  remaining: number;
  /** Cap reached — remove the tier from the paywall. */
  soldOut: boolean;
}

/** @param sold Sold count from stats/lifetime, or null when unavailable. */
export function lifetimeCountdown(sold: number | null): LifetimeCountdown {
  if (sold === null || !Number.isFinite(sold) || sold < 0) {
    // Unknown (offline / not configured): show the tier, claim nothing.
    return { showCountdown: false, remaining: LIFETIME_CAP, soldOut: false };
  }
  const remaining = Math.max(0, LIFETIME_CAP - Math.floor(sold));
  return {
    showCountdown: sold >= LIFETIME_SHOW_THRESHOLD && remaining > 0,
    remaining,
    soldOut: remaining === 0,
  };
}
